import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fullName, useEmployees, useFunctionAction } from "@/lib/data";
import type { UnassignedCard } from "./hooks";

export function AssignCardDialog({ card, onOpenChange }: { card: UnassignedCard | null; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const employees = useEmployees();
  const [employeeId, setEmployeeId] = useState("");
  const assign = useFunctionAction<Record<string, unknown>, { reprocessed: number }>("time-admin", {
    success: (r) => t("today.reprocessed", { count: r.reprocessed }),
  });

  return (
    <Dialog open={card !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("today.assign_title")}</DialogTitle>
          <DialogDescription>{t("today.assign_hint")}</DialogDescription>
        </DialogHeader>
        {card && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">
              <div className="font-medium">{card.actorName ?? card.actorId}</div>
              <div className="font-mono text-xs text-muted-foreground">{card.actorId}</div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.employee")}</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("times.select_employee")} />
                </SelectTrigger>
                <SelectContent>
                  {employees.data?.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {fullName(e)} {e.personnel_number && <span className="text-muted-foreground">· {e.personnel_number}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!employeeId || assign.isPending}
            onClick={() =>
              assign.mutate(
                { action: "assign_credential", employeeId, externalUserId: card!.actorId, externalUserName: card!.actorName },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            {t("today.assign")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
